import { database } from './firebase';
import { ref, set, update, onValue, serverTimestamp, get } from 'firebase/database';

/**
 * Game Service
 * Handles Firebase operations for all games
 */

/**
 * Create a new game
 * @param {string} roomCode - Room code
 * @param {string} gameType - Type of game (e.g., 'tictactoe')
 * @param {Object} initialGameState - Initial game state
 * @param {Object} players - Player information { host, guest }
 * @returns {Promise<boolean>} Success status
 */
export const createGame = async (roomCode, gameType, initialGameState, players) => {
  try {
    const gameRef = ref(database, `games/${roomCode}`);

    const gameData = {
      gameType,
      gameState: initialGameState,
      players,
      status: 'playing',
      createdAt: serverTimestamp(),
      lastMove: serverTimestamp(),
    };

    await set(gameRef, gameData);
    return true;
  } catch (error) {
    console.error('Error creating game:', error);
    return false;
  }
};

/**
 * Update game state
 * @param {string} roomCode - Room code
 * @param {Object} gameState - Updated game state
 * @returns {Promise<boolean>} Success status
 */
export const updateGameState = async (roomCode, gameState) => {
  try {
    const gameRef = ref(database, `games/${roomCode}/gameState`);
    await set(gameRef, gameState);

    // Update lastMove timestamp
    const lastMoveRef = ref(database, `games/${roomCode}/lastMove`);
    await set(lastMoveRef, serverTimestamp());

    return true;
  } catch (error) {
    console.error('Error updating game state:', error);
    return false;
  }
};

/**
 * Update game status
 * @param {string} roomCode - Room code
 * @param {string} status - New status ('playing', 'completed', 'rematch_requested')
 * @returns {Promise<boolean>} Success status
 */
export const updateGameStatus = async (roomCode, status) => {
  try {
    const statusRef = ref(database, `games/${roomCode}/status`);
    await set(statusRef, status);
    return true;
  } catch (error) {
    console.error('Error updating game status:', error);
    return false;
  }
};

/**
 * Subscribe to game updates
 * @param {string} roomCode - Room code
 * @param {Function} callback - Callback function to handle game updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToGame = (roomCode, callback) => {
  const gameRef = ref(database, `games/${roomCode}`);

  const unsubscribe = onValue(gameRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.val());
    } else {
      callback(null);
    }
  });

  return unsubscribe;
};

/**
 * Get game data
 * @param {string} roomCode - Room code
 * @returns {Promise<Object|null>} Game data or null
 */
export const getGame = async (roomCode) => {
  try {
    const gameRef = ref(database, `games/${roomCode}`);
    const snapshot = await get(gameRef);

    if (snapshot.exists()) {
      return snapshot.val();
    }

    return null;
  } catch (error) {
    console.error('Error getting game:', error);
    return null;
  }
};

/**
 * Request rematch
 * @param {string} roomCode - Room code
 * @param {string} playerId - Player ID requesting rematch
 * @returns {Promise<boolean>} Success status
 */
export const requestRematch = async (roomCode, playerId) => {
  try {
    const rematchRef = ref(database, `games/${roomCode}/rematchRequests/${playerId}`);
    await set(rematchRef, {
      timestamp: serverTimestamp(),
      requested: true,
    });

    return true;
  } catch (error) {
    console.error('Error requesting rematch:', error);
    return false;
  }
};

/**
 * Check if both players have requested rematch
 * @param {string} roomCode - Room code
 * @returns {Promise<boolean>} True if both players want rematch
 */
export const checkBothRematchRequested = async (roomCode) => {
  try {
    const rematchRef = ref(database, `games/${roomCode}/rematchRequests`);
    const snapshot = await get(rematchRef);

    if (!snapshot.exists()) {
      return false;
    }

    const requests = snapshot.val();
    const requestCount = Object.keys(requests).length;

    return requestCount === 2;
  } catch (error) {
    console.error('Error checking rematch requests:', error);
    return false;
  }
};

/**
 * Reset game for rematch
 * @param {string} roomCode - Room code
 * @param {string} gameType - Type of game
 * @param {Object} initialGameState - Fresh game state
 * @param {Object} players - Player information
 * @returns {Promise<boolean>} Success status
 */
export const resetGameForRematch = async (roomCode, gameType, initialGameState, players) => {
  try {
    const gameRef = ref(database, `games/${roomCode}`);

    const gameData = {
      gameType,
      gameState: initialGameState,
      players,
      status: 'playing',
      createdAt: serverTimestamp(),
      lastMove: serverTimestamp(),
      rematchRequests: {}, // Clear rematch requests
    };

    await set(gameRef, gameData);
    return true;
  } catch (error) {
    console.error('Error resetting game for rematch:', error);
    return false;
  }
};

/**
 * Delete game
 * @param {string} roomCode - Room code
 * @returns {Promise<boolean>} Success status
 */
export const deleteGame = async (roomCode) => {
  try {
    const gameRef = ref(database, `games/${roomCode}`);
    await set(gameRef, null);
    return true;
  } catch (error) {
    console.error('Error deleting game:', error);
    return false;
  }
};

/**
 * Save game to history
 * @param {string} userId - User ID
 * @param {Object} gameData - Game result data
 * @returns {Promise<boolean>} Success status
 */
export const saveGameToHistory = async (userId, gameData) => {
  try {
    const historyRef = ref(database, `gameHistory/${userId}/${Date.now()}`);

    await set(historyRef, {
      ...gameData,
      timestamp: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error('Error saving game to history:', error);
    return false;
  }
};

/**
 * Update match stats
 * @param {string} userId - User ID
 * @param {string} gameType - Type of game
 * @param {string} result - Result ('won', 'lost', 'draw')
 * @returns {Promise<boolean>} Success status
 */
export const updateMatchStats = async (userId, gameType, result) => {
  try {
    const statsRef = ref(database, `matchStats/${userId}/${gameType}`);
    const snapshot = await get(statsRef);

    let stats = {
      gamesPlayed: 0,
      wins: 0,
      losses: 0,
      draws: 0,
    };

    if (snapshot.exists()) {
      stats = snapshot.val();
    }

    stats.gamesPlayed += 1;

    if (result === 'won') {
      stats.wins += 1;
    } else if (result === 'lost') {
      stats.losses += 1;
    } else if (result === 'draw') {
      stats.draws += 1;
    }

    await set(statsRef, stats);
    return true;
  } catch (error) {
    console.error('Error updating match stats:', error);
    return false;
  }
};
